/* eslint-disable react-hooks/exhaustive-deps */
import React, { memo, useCallback, useEffect } from 'react';
import { GamificationEconomies } from '@domain/navigation/interactive-header/gamification-economies/gamification-economies';
import { useInterval } from '@shared/hooks/useInterval';
import { toCancellable } from '@shared/axios';
import { getEntityId, getEntityType } from '@shared/utils/entity';
import { getNextBoardName } from './interactive-header.utils';
import { AccountInfo } from './account-info';
import { EconomySavings } from './economy-savings/economy-savings';
import { LeaderBoard } from './leader-board';
import { REPORT_DATA_REFRESH_TIME } from './interactive-header.constants';
import { withInteractiveHeaderData } from './interactive-header.hocs';

const BOARD_COMPONENTS = {
    THIS_MONTH_METRICS: AccountInfo,
    THIS_DAY_LEADER_BOARD: LeaderBoard,
    THIS_WEEK_LEADER_BOARD: LeaderBoard,
    THIS_MONTH_LEADER_BOARD: LeaderBoard,
    THIS_QUARTER_LEADER_BOARD: LeaderBoard,
    THIS_YEAR_APPLIED_SAVINGS: EconomySavings,
    LAST_12_MONTHS_APPLIED_SAVINGS: EconomySavings,
    THIS_DAY_POTENTIAL_SAVINGS: EconomySavings,
    ALL_TIME_GAMIFICATION_ECONOMIES: GamificationEconomies,
};

const defaultProps = {
    boards: {},
    loadingState: {},
    refreshTime: 20 * 1000,
    isDesktop: false,
};

interface InteractiveHeaderImplProps {
    objectSelected: {
        loadingObjectSelected: boolean;
        ['client_selected']: { selected: boolean };
    };
    currentBoard: string;
    boards: {};
    loadingState: {};
    refreshTime: number;
    updateCurrentBoard: (boardName: string) => void;
    getReports: (entity: { entityType: string; entityId: number }) => void;
    isDesktop: boolean;
}

const InteractiveHeaderImpl = ({
    objectSelected,
    currentBoard,
    boards,
    loadingState,
    refreshTime,
    updateCurrentBoard,
    getReports,
    isDesktop,
}: InteractiveHeaderImplProps) => {
    const BoardComponent = BOARD_COMPONENTS[currentBoard] || null;
    const boardData = boards[currentBoard];

    const requestReports = useCallback(() => {
        if (objectSelected.loadingObjectSelected) return null;

        const entityType = getEntityType(objectSelected);
        const entityId = getEntityId(objectSelected);

        return getReports({ entityType, entityId });
    }, [objectSelected]);

    useEffect(() => toCancellable(requestReports()), [objectSelected]);
    useEffect(() => {
        if (!currentBoard) {
            const nextBoard = getNextBoardName(boards, currentBoard);
            updateCurrentBoard(nextBoard);
        }
    }, [boards, objectSelected]);

    useInterval(
        () => updateCurrentBoard(getNextBoardName(boards, currentBoard)),
        refreshTime,
    );
    useInterval(() => requestReports(), REPORT_DATA_REFRESH_TIME);

    return (
        BoardComponent && (
            <BoardComponent
                boardName={currentBoard}
                boardData={boardData}
                loading={!!loadingState[currentBoard]}
                isDesktop={isDesktop}
            />
        )
    );
};

InteractiveHeaderImpl.defaultProps = defaultProps;

export const InteractiveHeader = withInteractiveHeaderData(memo(InteractiveHeaderImpl));
